import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import api from '@/axios';
import type { CreateLotSizingLine } from '@/features/lot-sizing/schema/lot-sizing-add-line-schema';
import { GET_LOT_SIZING_KEY, GET_LOT_SIZING_LINES_KEY } from '@/keys';
import type { LotSizingLine } from '@/types/core/lot-sizing/lot-sizing-line';

type CreateLotSizingLineResponse = LotSizingLine;

type DraftLotSizingLineResponse = Omit<LotSizingLine, 'id'> & {
  id: null;
};

export type CreateLotSizingLineResult = Omit<LotSizingLine, 'id'> | null;

const createLotSizingLine = async ({
  lotSizingId,
  productId,
}: CreateLotSizingLine): Promise<CreateLotSizingLineResult> => {
  const { data } = await api.post<
    CreateLotSizingLineResponse | DraftLotSizingLineResponse
  >('/lot-sizing-lines', {
    lotSizingId,
    productId,
  });

  return data.id === null
    ? {
        supplierId: data.supplierId,
        unitPrice: data.unitPrice,
        unitVolume: data.unitVolume,
        quantity: data.quantity,
        checked: data.checked,
        product: data.product,
        purchasePlanning: data.purchasePlanning,
        productImportPackage: data.productImportPackage,
      }
    : null;
};

export function useCreateLotSizingLine(): UseMutationResult<
  CreateLotSizingLineResult,
  Error,
  CreateLotSizingLine
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLotSizingLine,
    onSuccess: async (data, { lotSizingId }) => {
      if (data !== null) {
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: [...GET_LOT_SIZING_LINES_KEY, lotSizingId],
      });
      await queryClient.invalidateQueries({
        queryKey: [...GET_LOT_SIZING_KEY, lotSizingId],
      });
    },
  });
}
